import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { auth } from "../firebase/config";
import { functions } from "../firebase/config";
import { httpsCallable } from "firebase/functions";
import {
  createUserWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
} from "firebase/auth";
import { FaArrowLeft } from "react-icons/fa";
import googleLogo from "../assets/googlelogo.webp";
import "./signup.css";
import { setPageMeta } from "../utils/seo";

const SignupForm = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setPageMeta({
      title: "Create Account | Banat India",
      description:
        "Create a Banat India account for faster checkout and order tracking.",
      keywords: "sign up, create account, Banat India register, customer account",
    });
  }, []);

  const handleEmailSignup = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    // Validate passwords match
    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      setLoading(false);
      return;
    }

    // Validate password length
    if (password.length < 6) {
      setError("Password should be at least 6 characters.");
      setLoading(false);
      return;
    }

    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      
      // Send welcome email
      if (userCredential && userCredential.user) {
        try {
          const sendWelcomeEmail = httpsCallable(functions, "sendWelcomeEmail");
          const emailResult = await sendWelcomeEmail({
            email: userCredential.user.email,
            displayName: userCredential.user.displayName || userCredential.user.email,
          });
          
          if (emailResult.data.success) {
            console.log("Welcome email sent successfully");
          } else {
            console.warn("Welcome email failed:", emailResult.data.error);
          }
        } catch (err) {
          console.error("Failed to send welcome email:", err);
          // Don't block the sign-up process if email fails
        }
      }
      
      navigate("/dashboard?tab=profile");
    } catch (error) {
      let errorMessage = "An error occurred. Please try again.";
      if (error.code === "auth/email-already-in-use") {
        errorMessage = "This email is already registered. Please sign in instead.";
      } else if (error.code === "auth/invalid-email") {
        errorMessage = "Invalid email address.";
      } else if (error.code === "auth/weak-password") {
        errorMessage = "Password should be at least 6 characters.";
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      setLoading(true);
      setError("");
      const provider = new GoogleAuthProvider();
      
      // Add custom parameters to avoid popup issues
      provider.setCustomParameters({
        prompt: 'select_account'
      });
      
      const result = await signInWithPopup(auth, provider);
      
      // Send welcome email
      if (result && result.user) {
        try {
          const sendWelcomeEmail = httpsCallable(functions, "sendWelcomeEmail");
          const emailResult = await sendWelcomeEmail({
            email: result.user.email,
            displayName: result.user.displayName || result.user.email,
          });
          
          if (emailResult.data.success) {
            console.log("Welcome email sent successfully");
          } else {
            console.warn("Welcome email failed:", emailResult.data.error);
          }
        } catch (err) {
          console.error("Failed to send welcome email:", err);
          // Don't block the sign-in process if email fails
        }
      }
      
      navigate("/dashboard?tab=profile");
    } catch (error) {
      console.error("Google sign-in error:", error);
      let errorMessage = "Error signing in with Google. Please try again.";
      
      // Handle specific popup-related errors
      if (error.code === "auth/popup-closed-by-user") {
        errorMessage = "Sign-in was cancelled. Please try again.";
      } else if (error.code === "auth/popup-blocked") {
        errorMessage = "Popup was blocked by your browser. Please allow popups for this site and try again.";
      } else if (error.code === "auth/cancelled-popup-request") {
        errorMessage = "Sign-in was cancelled. Please try again.";
      } else if (error.code === "auth/account-exists-with-different-credential") {
        errorMessage = "An account already exists with this email using a different sign-in method.";
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="signup-container">
      <div className="signup-card">
        <button className="back-button" onClick={() => navigate("/")}>
          <FaArrowLeft /> Back
        </button>

        <div className="banat-login-logo">
          <img src="/logo.png" alt="Banat India logo" className="banat-logo" />
        </div>

        <h2>Create Account</h2>
        <p className="signup-subtitle">Join Banat to explore our products</p>

        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleEmailSignup} className="signup-form">
          <div className="form-group">
            <label>Email Address</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email address"
              required
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label>Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Create a password"
              required
              disabled={loading}
              minLength="6"
            />
            <small className="input-hint">Password should be at least 6 characters</small>
          </div>

          <div className="form-group">
            <label>Confirm Password</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm your password"
              required
              disabled={loading}
              minLength="6"
            />
          </div>

          <button
            type="submit"
            className="signup-button"
            disabled={loading || !email || !password || !confirmPassword}
          >
            {loading ? "Creating Account..." : "Create Account"}
          </button>
        </form>

        <div className="divider">
          <span>or</span>
        </div>

        <button 
          className="google-button"
          onClick={handleGoogleSignIn}
          disabled={loading}
        >
          <img src={googleLogo} alt="Google logo" />
          Continue with Google
        </button>

        <p className="login-link">
          Already have an account?{" "}
          <button className="link-button" onClick={() => navigate("/login")}>
            Sign In
          </button>
        </p>
      </div>
    </div>
  );
};

export default SignupForm;
